import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Bird, MapPin, Binoculars, Calendar, MagnifyingGlass, CaretLeft,
  Warning, ArrowSquareOut, Eye, MapTrifold
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from "react-leaflet";
import { RecenterMap } from "@/components/MapHelpers";
import { API } from "@/config/constants";
import L from "leaflet";

const hotspotIcon = new L.Icon({
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
  className: "hue-rotate-[120deg] saturate-150"
});

const EBirdPage = () => {
  const navigate = useNavigate();
  const [configured, setConfigured] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lat, setLat] = useState("40.7128");
  const [lng, setLng] = useState("-74.0060");
  const [dist, setDist] = useState("25");
  const [hotspots, setHotspots] = useState([]);
  const [observations, setObservations] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [activeTab, setActiveTab] = useState("hotspots");
  const [searchPerformed, setSearchPerformed] = useState(false);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API}/ebird/status`);
        setConfigured(res.data.configured);
      } catch (error) {
        setConfigured(false);
      } finally {
        setLoading(false);
      }
    };
    checkStatus();
  }, []);

  const handleUseMyLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLat(pos.coords.latitude.toFixed(4));
          setLng(pos.coords.longitude.toFixed(4));
          toast.success("Location updated!");
        },
        () => toast.error("Could not get location")
      );
    }
  };

  const handleSearch = useCallback(async () => {
    if (!lat || !lng) { toast.error("Enter coordinates first"); return; }
    setFetching(true);
    setSearchPerformed(true);
    try {
      const [hotspotsRes, obsRes] = await Promise.all([
        axios.get(`${API}/ebird/hotspots?lat=${lat}&lng=${lng}&dist=${dist}`),
        axios.get(`${API}/ebird/recent-observations?lat=${lat}&lng=${lng}&dist=${dist}`)
      ]);
      setHotspots(hotspotsRes.data.hotspots || []);
      setObservations(obsRes.data.observations || []);
      if (hotspotsRes.data.message) toast.info(hotspotsRes.data.message);
    } catch (error) {
      toast.error("Failed to fetch eBird data");
    } finally {
      setFetching(false);
    }
  }, [lat, lng, dist]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto page-content">
        <Skeleton className="h-10 w-64 mb-6 bg-[#EAE7DE]" />
        <Skeleton className="h-64 w-full bg-[#EAE7DE] rounded-lg" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="ebird-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate("/map")} className="text-white" data-testid="back-to-map">
          <CaretLeft size={20} />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-white">eBird Explorer</h1>
            <Badge className="bg-[#C25A3C] text-white text-xs">
              <Binoculars size={12} className="mr-1" />eBird
            </Badge>
          </div>
          <p className="text-xs sm:text-base text-white/70 mt-1">Discover birding hotspots and recent sightings near any location</p>
        </div>
      </div>

      {!configured && (
        <Card className="bg-[#FFF3CD] border-[#FFE69C] mb-6">
          <CardContent className="p-4 flex items-start gap-3">
            <Warning size={24} className="text-[#856404] shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-[#856404]">eBird API Key Not Configured</p>
              <p className="text-sm text-[#856404]/80 mt-1">
                To enable live eBird data, add your free API key to <code className="bg-[#856404]/10 px-1 rounded">backend/.env</code> as <code className="bg-[#856404]/10 px-1 rounded">EBIRD_API_KEY=your_key</code>.
                Get a free key at <a href="https://ebird.org/api/keygen" target="_blank" rel="noopener noreferrer" className="underline font-medium">ebird.org/api/keygen</a>.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search Controls */}
      <Card className="bg-white border-[#D2C2B3] mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-xs font-medium text-white mb-1">Latitude</label>
              <Input value={lat} onChange={(e) => setLat(e.target.value)} placeholder="40.7128" className="bg-[#EAE7DE] border-[#D2C2B3]" data-testid="ebird-lat" />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-white mb-1">Longitude</label>
              <Input value={lng} onChange={(e) => setLng(e.target.value)} placeholder="-74.0060" className="bg-[#EAE7DE] border-[#D2C2B3]" data-testid="ebird-lng" />
            </div>
            <div className="w-24">
              <label className="block text-xs font-medium text-white mb-1">Radius (km)</label>
              <Input value={dist} onChange={(e) => setDist(e.target.value)} placeholder="25" type="number" className="bg-[#EAE7DE] border-[#D2C2B3]" data-testid="ebird-dist" />
            </div>
            <Button variant="outline" onClick={handleUseMyLocation} className="bg-[#55624F] border-[#55624F] text-white" data-testid="ebird-use-location">
              <MapPin size={18} />
            </Button>
            <Button onClick={handleSearch} disabled={fetching || !configured} className="bg-[#55624F] hover:bg-[#424D3D] text-white min-w-[120px]" data-testid="ebird-search">
              {fetching ? "Searching..." : <><MagnifyingGlass size={18} className="mr-2" />Search</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {searchPerformed && (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 bg-[#EAE7DE]">
            <TabsTrigger value="hotspots" className="data-[state=active]:bg-[#55624F] data-[state=active]:text-white" data-testid="tab-hotspots">
              <MapTrifold size={16} className="mr-1" />Hotspots ({hotspots.length})
            </TabsTrigger>
            <TabsTrigger value="observations" className="data-[state=active]:bg-[#C25A3C] data-[state=active]:text-white" data-testid="tab-observations">
              <Eye size={16} className="mr-1" />Recent Sightings ({observations.length})
            </TabsTrigger>
            <TabsTrigger value="map" className="data-[state=active]:bg-[#55624F] data-[state=active]:text-white" data-testid="tab-map">
              <MapPin size={16} className="mr-1" />Map View
            </TabsTrigger>
          </TabsList>

          {/* Hotspots Tab */}
          <TabsContent value="hotspots">
            {hotspots.length === 0 ? (
              <Card className="bg-[#EAE7DE] border-[#D2C2B3]">
                <CardContent className="p-8 text-center">
                  <Binoculars size={48} weight="duotone" className="mx-auto mb-4 text-[#7C8068]" />
                  <p className="text-[#7C8068]">{configured ? "No hotspots found in this area. Try a larger radius." : "Configure your eBird API key to see live data."}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {hotspots.map(hs => (
                  <Card key={hs.id} className="bg-white border-[#D2C2B3] card-hover" data-testid={`hotspot-${hs.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-[#55624F]/10 rounded-lg shrink-0">
                          <MapTrifold size={22} weight="duotone" className="text-[#55624F]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-[#2E332B] truncate">{hs.name}</h3>
                          <p className="text-xs text-[#7C8068] mt-1">{hs.region || hs.country}</p>
                          {hs.species_count > 0 && (
                            <Badge className="bg-[#EAE7DE] text-[#55624F] text-xs mt-2">
                              <Bird size={10} className="mr-1" />{hs.species_count} species
                            </Badge>
                          )}
                          {hs.latest_obs && (
                            <p className="text-xs text-[#7C8068] mt-1 flex items-center gap-1">
                              <Calendar size={10} />Last obs: {hs.latest_obs}
                            </p>
                          )}
                        </div>
                        <a
                          href={`https://ebird.org/hotspot/${hs.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#55624F] hover:text-[#C25A3C] transition-colors"
                          title="View on eBird"
                        >
                          <ArrowSquareOut size={18} />
                        </a>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Observations Tab */}
          <TabsContent value="observations">
            {observations.length === 0 ? (
              <Card className="bg-[#EAE7DE] border-[#D2C2B3]">
                <CardContent className="p-8 text-center">
                  <Bird size={48} weight="duotone" className="mx-auto mb-4 text-[#7C8068]" />
                  <p className="text-[#7C8068]">{configured ? "No recent observations in this area." : "Configure your eBird API key to see live data."}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {observations.map((obs, idx) => (
                  <Card key={`${obs.species_code}-${idx}`} className="bg-white border-[#D2C2B3]" data-testid={`observation-${idx}`}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="p-2 bg-[#C25A3C]/10 rounded-lg">
                        <Bird size={22} weight="duotone" className="text-[#C25A3C]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-[#2E332B]">{obs.common_name}</h3>
                        <p className="text-xs text-[#7C8068] italic">{obs.scientific_name}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-[#7C8068]">
                          <span className="flex items-center gap-1"><MapPin size={10} />{obs.location_name}</span>
                          <span className="flex items-center gap-1"><Calendar size={10} />{obs.observation_date}</span>
                          {obs.count > 0 && <Badge className="bg-[#EAE7DE] text-[#55624F] text-xs">{obs.count} seen</Badge>}
                        </div>
                      </div>
                      <a
                        href={`https://ebird.org/species/${obs.species_code}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#55624F] hover:text-[#C25A3C] transition-colors"
                        title="View on eBird"
                      >
                        <ArrowSquareOut size={18} />
                      </a>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Map Tab */}
          <TabsContent value="map">
            <div className="h-[500px] rounded-lg overflow-hidden border border-[#D2C2B3]">
              <MapContainer center={[parseFloat(lat), parseFloat(lng)]} zoom={10} className="h-full w-full">
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <RecenterMap lat={parseFloat(lat)} lng={parseFloat(lng)} zoom={10} />

                {/* Search center */}
                <CircleMarker center={[parseFloat(lat), parseFloat(lng)]} radius={8} pathOptions={{ color: "#C25A3C", fillColor: "#C25A3C", fillOpacity: 0.8 }}>
                  <Popup>Search center</Popup>
                </CircleMarker>

                {/* Hotspots */}
                {hotspots.map(hs => (
                  <Marker key={hs.id} position={[hs.latitude, hs.longitude]} icon={hotspotIcon}>
                    <Popup>
                      <div className="max-w-[200px]">
                        <h4 className="font-semibold text-sm">{hs.name}</h4>
                        {hs.species_count > 0 && <p className="text-xs text-gray-600">{hs.species_count} species</p>}
                        <a href={`https://ebird.org/hotspot/${hs.id}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline">View on eBird</a>
                      </div>
                    </Popup>
                  </Marker>
                ))}

                {/* Recent observations */}
                {observations.map((obs, idx) => (
                  <CircleMarker key={`obs-${idx}`} center={[obs.latitude, obs.longitude]} radius={5} pathOptions={{ color: "#D99559", fillColor: "#D99559", fillOpacity: 0.7 }}>
                    <Popup>
                      <div className="max-w-[200px]">
                        <h4 className="font-semibold text-sm">{obs.common_name}</h4>
                        <p className="text-xs text-gray-500 italic">{obs.scientific_name}</p>
                        <p className="text-xs text-gray-600">{obs.observation_date}</p>
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
              </MapContainer>
            </div>
            <div className="flex items-center gap-6 mt-3 text-xs text-[#7C8068]">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#C25A3C] inline-block" />Search center</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#55624F] inline-block" />Birding hotspots</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#D99559] inline-block" />Recent sightings</span>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default EBirdPage;
